/**
 * Address Card Component
 *
 * Displays an address as a card with edit/delete actions.
 * Used to show saved addresses in a visual card layout.
 */

import React from 'react'

import { Button } from '../ui'

import type { Address } from '../../types/billing'

export interface AddressCardProps {
  address: Address
  selected?: boolean
  onSelect?: () => void
  onEdit?: () => void
  onDelete?: () => void
  showActions?: boolean
  deleteDisabled?: boolean
}

function PrimaryBadge(): React.ReactElement {
  return (
    <div className="absolute top-2 right-2">
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-solid text-primary-foreground">Primary</span>
    </div>
  )
}

function SelectedIndicator(): React.ReactElement {
  return (
    <div className="absolute bottom-2 right-2">
      <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 20 20">
        <path clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" fillRule="evenodd" />
      </svg>
    </div>
  )
}

function AddressDetails({ address }: { address: Address }): React.ReactElement {
  return (
    <div className="text-sm text-foreground space-y-1">
      <p>{address.line1}</p>
      {address.line2 && <p>{address.line2}</p>}
      <p>{address.city}, {address.state} {address.postalCode}</p>
      <p>{address.country}</p>
      {address.gstin && <p className="text-xs text-foreground-secondary mt-2">GSTIN: {address.gstin}</p>}
      {address.companyCode && <p className="text-xs text-foreground-secondary">Company Code: {address.companyCode}</p>}
    </div>
  )
}

interface ActionButtonsProps {
  onEdit?: () => void
  onDelete?: () => void
  isDeleteDisabled: boolean
}

function EditButton({ onClick }: { onClick: () => void }): React.ReactElement {
  return (
    <Button
      size="sm"
      type="button"
      variant="secondary"
      onClick={onClick}
    >
      Edit
    </Button>
  )
}

function DeleteButton({ onClick, isDisabled }: { onClick: () => void; isDisabled: boolean }): React.ReactElement {
  return (
    <Button
      disabled={isDisabled}
      size="sm"
      title={isDisabled ? 'Cannot delete primary address' : 'Delete address'}
      type="button"
      variant="danger"
      onClick={onClick}
    >
      Delete
    </Button>
  )
}

function ActionButtons({ onEdit, onDelete, isDeleteDisabled }: ActionButtonsProps): React.ReactElement | null {
  const handleEditClick = (): void => {
    onEdit?.()
  }

  const handleDeleteClick = (): void => {
    onDelete?.()
  }

  if (!onEdit && !onDelete) { return null }

  return (
    <div className="mt-4 flex gap-2">
      {onEdit && <EditButton onClick={handleEditClick} />}
      {onDelete && <DeleteButton isDisabled={isDeleteDisabled} onClick={handleDeleteClick} />}
    </div>
  )
}

function getCardClass(selected: boolean): string {
  return selected
    ? 'border-primary bg-muted shadow-md'
    : 'border-border bg-card hover:border-primary hover:shadow-sm'
}

export function AddressCard({
  address,
  selected = false,
  onSelect,
  onEdit,
  onDelete,
  showActions = true,
  deleteDisabled = false
}: AddressCardProps): React.ReactElement {
  const handleClick = (): void => { onSelect?.() }
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') { onSelect?.() }
  }

  return (
    <div
      className={`relative p-4 rounded-lg border-2 transition-all cursor-pointer ${getCardClass(selected)}`}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {address.isPrimary && <PrimaryBadge />}
      <AddressDetails address={address} />
      {showActions && <ActionButtons isDeleteDisabled={deleteDisabled} onDelete={onDelete} onEdit={onEdit} />}
      {selected && <SelectedIndicator />}
    </div>
  )
}
